import React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import NoImage from 'assets/no-image.jpg'
import Icon from 'components/Icon'
import LocationIndicator, { LocationTypeKeys } from 'components/LocationIndicator'
import { ShowItem } from 'generated/graphql'
import { useDeleteItem, useEventSlug } from 'hooks'
import { sponsorSelector, timeFormatSelector } from 'features/events/eventSlice'
import { _ } from 'utils'
import routes from '../routes'
import { deleteShow } from '../graphql/mutation'
import { S } from './ProgramContainerRow'
import SponsorField from './SponsorField'

interface Props {
    row: ShowItem
    fetchData?: () => void
    parentColor?: string
}

export const formatDate = (startDate, endDate, timeFormat) => (
    `${format(startDate, `E dd MMM, ${timeFormat}`)}-${format(endDate, timeFormat)}`
)

export const getLocationType = (item: ShowItem): LocationTypeKeys => {
    if (item.mapitem && item.virtualSpace) {
        return 'HYBRID'
    }
    if (item.mapitem) {
        return 'IN_PERSON'
    }
    if (item.virtualSpace) {
        return 'VIRTUAL'
    }
}

const ProgramRow = ({ row, fetchData, parentColor }: Props) => {
    const eventSlug = useEventSlug()
    const timeFormat = useSelector(timeFormatSelector)
    const sponsors = useSelector(sponsorSelector)
    const rowSpaceName = row?.virtualSpace || row?.mapitem

    const deleteShowFn = () => deleteShow({ filters: { id: row.id } })
    const { showDeleteConfirmModal } = useDeleteItem(deleteShowFn, fetchData)

    return (
        <S.Wrapper parentColor={parentColor}>
            <S.Image image={row.virtualImage || row.image || NoImage} />
            <div style={{ overflow: 'hidden' }}>
                <S.Title color={row.color}>
                    {row.startDate && formatDate(
                        new Date(row.startDate), new Date(row.endDate), timeFormat
                    )}
                </S.Title>
                <S.Location>
                    {rowSpaceName ? (
                        <>
                            <Icon icon="mapMarker" size={16} className="d-flex" />
                            {rowSpaceName}
                        </>
                    ) : <div className="ml-1">{_('show.list.item.no_space')}</div>}
                </S.Location>
            </div>
            <div style={{ minWidth: 0 }}>
                <S.Description className="text-cut">
                    {row.name}
                </S.Description>
                <LocationIndicator location={getLocationType(row)} />
            </div>
            <SponsorField id={row.id} sponsors={sponsors} value={row.sponsor} fetchData={fetchData} />
            <S.Actions className="program-row-actions" style={{ display: 'none' }}>
                <Link to={routes.updateShow.getPath({ eventSlug, id: row.id })}>
                    <Icon icon="edit" size={18} />
                </Link>
                <Icon icon="trash" className="ml-2" size={18} onClick={showDeleteConfirmModal} />
            </S.Actions>
        </S.Wrapper>
    )
}

export default ProgramRow
